<template>
    <div class="private-layout">
        <!-- Menu lateral: fixo em telas grandes, gaveta no celular/tablet -->
        <v-navigation-drawer app v-model="drawer" :permanent="isDesktop" :temporary="!isDesktop" width="264"
            color="navyDeep" dark class="app-sidebar">
            <router-link to="/home" class="app-sidebar__brand" aria-label="Ir para o início">
                <img src="@/assets/logo-pbb-white.png" alt="Paoli Balbino & Balbino Administração Judicial" />
                <span>Painel administrativo</span>
            </router-link>

            <nav class="app-sidebar__nav" aria-label="Menu principal">
                <template v-for="(menuGroup, groupIndex) in visibleMenu">
                    <div :key="`group-${groupIndex}`" class="app-sidebar__group">{{ menuGroup.name }}</div>
                    <v-list :key="`list-${groupIndex}`" dense nav class="app-sidebar__list">
                        <MenuItem v-for="(menuOption, index) in menuGroup.children" :key="index" :item="menuOption" />
                    </v-list>
                </template>
            </nav>
        </v-navigation-drawer>

        <v-app-bar app flat color="white" height="64" class="app-topbar">
            <v-app-bar-nav-icon v-if="!isDesktop" color="primary" aria-label="Abrir menu"
                @click.stop="drawer = !drawer"></v-app-bar-nav-icon>

            <div class="app-topbar__section">{{ currentSection }}</div>

            <v-spacer></v-spacer>

            <v-menu min-width="220" offset-y left>
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="app-topbar__user" v-bind="attrs" v-on="on">
                        <span class="app-topbar__user-text d-none d-sm-flex">
                            <span class="app-topbar__user-name">{{ user.name }}</span>
                            <span class="app-topbar__user-email">{{ user.email }}</span>
                        </span>
                        <v-avatar size="38" color="primary">
                            <span class="white--text app-topbar__initials">{{ initials }}</span>
                        </v-avatar>
                        <v-icon size="20" color="muted">mdi-chevron-down</v-icon>
                    </button>
                </template>
                <v-list dense>
                    <MenuItem v-for="(menuOption, index) in profileMenu" :key="index" :item="menuOption" />
                </v-list>
            </v-menu>
        </v-app-bar>

        <div class="private-layout__content">
            <router-view></router-view>
        </div>
    </div>
</template>

<script>
import { eventHub } from "@/main";

import router from '../router';

import MenuItem from "@/layouts/components/MenuItem";
import profileMenu from "@/common/data/profile-menu.data"
import sidebarMenu from "@/common/data/sidebar-menu.data"

export default {
    name: "PrivateLayout",

    components: {
        MenuItem
    },

    data() {
        return {
            drawer: null,
            user: {},
            sidebarMenu: sidebarMenu,
            profileMenu: profileMenu
        };
    },

    computed: {
        isDesktop() {
            return this.$vuetify.breakpoint.lgAndUp;
        },

        // Grupos e itens que o perfil do administrador pode ver
        visibleMenu() {
            return this.sidebarMenu
                .filter((group) => !group.path || this.$check(group.path, 'GET'))
                .map((group) => ({
                    ...group,
                    children: group.children.filter((item) => !group.path || !item.path || this.$check(item.path, 'GET')),
                }))
                .filter((group) => group.children.length);
        },

        // Nome da área atual, pelo item de menu que mais combina com a rota
        currentSection() {
            const path = this.$route.path;
            let found = null;

            for (const group of this.sidebarMenu) {
                for (const item of group.children) {
                    if (!item.route) continue;
                    const matches = path === item.route || path.startsWith(`${item.route}/`);
                    if (matches && (!found || item.route.length > found.route.length)) found = item;
                }
            }

            return found ? found.name : "Painel administrativo";
        },

        initials() {
            const parts = String(this.user.name || "").trim().split(/\s+/).filter(Boolean);
            if (!parts.length) return "?";
            return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
        },
    },

    created() {
        this.user = this.$root.user.data || this.$root.user || {};

        eventHub.$on("logout", this.logout);
    },

    destroyed() {
        eventHub.$off("logout", this.logout);
    },

    methods: {
        logout() {
            localStorage.clear();
            router.push("/login");
        },
    },
};
</script>

<style scoped lang="scss">
.private-layout {
    min-height: 100%;
}

.private-layout__content {
    min-height: calc(100vh - 64px);
}

.app-sidebar {
    border-right: 0 !important;
}

.app-sidebar__brand {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    padding: 24px 22px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    text-decoration: none;

    img {
        height: 58px;
        width: auto;
    }

    span {
        font-size: 11px;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: var(--v-copper-base);
    }
}

.app-sidebar__nav {
    padding: 8px 8px 24px;
}

.app-sidebar__group {
    padding: 18px 14px 6px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.45);
}

.app-sidebar__list {
    padding: 0 !important;
    background: transparent !important;
}

.app-topbar {
    border-bottom: 1px solid var(--v-lightGray-base) !important;
}

.app-topbar__section {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-primary-base);
}

.app-topbar__user {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 6px 4px 10px;
    border-radius: 999px;
    background: transparent;
    cursor: pointer;

    &:hover {
        background: var(--v-gray-base);
    }
}

.app-topbar__user-text {
    flex-direction: column;
    align-items: flex-end;
    line-height: 1.25;
}

.app-topbar__user-name {
    font-size: 13px;
    font-weight: 700;
    color: var(--v-ink-base);
    text-transform: capitalize;
}

.app-topbar__user-email {
    font-size: 12px;
    color: var(--v-muted-base);
}

.app-topbar__initials {
    font-size: 14px;
    font-weight: 700;
}
</style>

<style lang="scss">
// Itens do menu lateral (fora do scoped para alcançar o v-list-item do MenuItem)
.app-sidebar .v-list-item {
    min-height: 38px !important;
    margin-bottom: 2px;
    border-radius: 8px !important;
    color: rgba(255, 255, 255, 0.78) !important;

    .v-list-item__title {
        font-size: 14px !important;
        font-weight: 500 !important;
    }

    .v-list-item__icon {
        margin-right: 14px !important;

        .v-icon {
            color: rgba(255, 255, 255, 0.55) !important;
            font-size: 20px;
        }
    }

    &:hover {
        background: rgba(255, 255, 255, 0.06);
    }

    &.v-list-item--active {
        background: rgba(201, 132, 80, 0.16);
        color: #fff !important;

        &::before {
            opacity: 0 !important;
        }

        .v-icon {
            color: var(--v-copper-base) !important;
        }
    }
}
</style>

<style lang="scss">
// Barra de rolagem discreta no menu lateral escuro
.app-sidebar .v-navigation-drawer__content {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.18) transparent;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-thumb {
        border-radius: 3px;
        background: rgba(255, 255, 255, 0.18);
    }
}
</style>
